/**
 * Underbarrel launcher — a real alt-fire, not a second trigger.
 *
 * Four things separate a launcher from "the gun, but explosive", and all four are
 * here because dropping any one of them turns the attachment into a free extra
 * weapon:
 *
 *  1. **Its own ammunition.** Two rounds, no reserve, no reload. When they are
 *     gone the attachment is dead weight for the rest of the life.
 *  2. **Its own switch time.** Pressing the alt-fire key takes the primary out of
 *     action for `deploy` seconds before the round leaves, and `stow` seconds
 *     afterwards. That is a second of standing there, and it is what stops the
 *     launcher being the answer to every close-range surprise.
 *  3. **An arc.** The round is a projectile with gravity at 34 m/s, so hitting
 *     anything past twenty metres is an aiming problem rather than a click.
 *  4. **A payload that matters at range, not in your face.** The grenade arms
 *     after a short flight; before that it bounces off inert. Point-blank self-
 *     detonation is the classic way this attachment becomes a panic button.
 *
 * Smoke is the other payload, and it is the interesting one: see `Smoke.js` for the
 * volume it deploys and what actually respects it.
 *
 * The primary is locked out through `Overheat.hold()`, which drives the weapon
 * system's own `nextShotAt` gate — the same mechanism, and the same reasoning about
 * why it is done from the outside, is documented there.
 */

import * as THREE from 'three';
import { MASK } from '../../world/Physics.js';
import { MEANS } from '../MatchAuthority.js';

export const TUNING = {
  /** Input action for the alt-fire. `grenade` (KeyG) is bound and unused. */
  action: 'grenade',

  gravity: 19.0,
  /** Launch angle above the aim line, radians. A launcher sits under the barrel
   *  and is zeroed high; without this the arc starts by dropping. */
  loft: 0.035,
  /** Metres in front of the eye the round starts, so it clears the viewmodel. */
  spawnAhead: 0.6,
  spawnDown: 0.12,

  /** Grenade arms after this much flight time. Before it, impacts just stop it. */
  armTime: 0.22,
  /** Self-destruct so a round lost in geometry cannot leak a slot. */
  maxFlight: 8.0,
  /** Smoke canister: seconds on the ground before it blooms. */
  smokeFuse: 0.9,

  radius: 0.055, // visual radius of the round
  maxRounds: 6,
  /** Sub-steps per frame for the flight raycast. Two is enough at 34 m/s and 60 Hz
   *  to never tunnel a 0.5 m wall. */
  substeps: 2,

  explosionPower: 1.15,
  /** Splash on the player is reduced but not removed — firing a launcher at your
   *  own feet has to hurt or the arc costs nothing. */
  selfDamageScale: 0.6,
  splashMinFraction: 0.15,
};

const SOLID = MASK.WORLD | MASK.PROP;
const STATE = { IDLE: 'idle', DEPLOYING: 'deploying', STOWING: 'stowing' };

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _from = new THREE.Vector3();

export class Launcher {
  constructor(ctx, system, smoke) {
    this.ctx = ctx;
    this.system = system;
    this.smoke = smoke;

    this.state = STATE.IDLE;
    this._timer = 0;
    this.fired = 0;

    /* ---- pooled rounds: one instanced-free group of tiny meshes, built lazily ---- */
    this.rounds = [];
    this._group = null;
    this._scratch = [];

    this._published = null;
  }

  get config() {
    return this.system.config.launcher;
  }

  onConfigChanged() {
    if (this.config.mode === 'none' && this.state !== STATE.IDLE) {
      this.state = STATE.IDLE;
      this._timer = 0;
    }
    this._publish(true);
  }

  /* --------------------------------- meshes -------------------------------- */

  _ensurePool() {
    if (this._group) return;
    const g = new THREE.Group();
    g.name = 'behaviour-launcher-rounds';
    this.ctx.scene.add(g);
    const geo = new THREE.SphereGeometry(TUNING.radius, 8, 6);
    // Two materials so a smoke canister reads differently in flight from a HE
    // round; both are shared across the pool.
    const heMat = new THREE.MeshStandardMaterial({ color: 0x2c3128, roughness: 0.72, metalness: 0.35 });
    const smokeMat = new THREE.MeshStandardMaterial({ color: 0x6d6f60, roughness: 0.6, metalness: 0.3 });
    this._mats = { grenade: heMat, smoke: smokeMat };
    this._geo = geo;
    for (let i = 0; i < TUNING.maxRounds; i++) {
      const m = new THREE.Mesh(geo, heMat);
      m.visible = false;
      m.castShadow = false;
      g.add(m);
      this.rounds.push({
        live: false,
        mesh: m,
        mode: 'grenade',
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        t: 0,
        grounded: false,
        fuse: 0,
      });
    }
    this._group = g;
  }

  /* --------------------------------- frame --------------------------------- */

  update(dt, elapsed) {
    const cfg = this.config;
    const anyLive = this._liveCount() > 0;
    if (cfg.mode === 'none' && this.state === STATE.IDLE && !anyLive) return;

    this._handleInput(elapsed);
    this._advanceState(dt, elapsed);
    if (anyLive) this._stepRounds(dt);
  }

  _liveCount() {
    let n = 0;
    for (let i = 0; i < this.rounds.length; i++) if (this.rounds[i].live) n++;
    return n;
  }

  _handleInput(elapsed) {
    const cfg = this.config;
    if (this.state !== STATE.IDLE) return;
    if (cfg.mode === 'none' || cfg.ammo <= 0) return;
    const input = this.ctx.input;
    const p = this.ctx.player;
    if (!input || !p || p.dead) return;
    if (!input.actionPressed?.(TUNING.action)) return;
    // Not mid-sprint: the launcher comes up from the same low-ready the rifle
    // does, and a sprinting player has nothing in their hands to bring up.
    if (p.sprinting || p.tacSprinting) return;
    this.state = STATE.DEPLOYING;
    this._timer = Math.max(0.01, cfg.deploy);
    this.ctx.events.emit('audio:play', { id: 'weapon_switch', volume: 0.5, pitch: 0.85 });
    this._publish(true);
    void elapsed;
  }

  _advanceState(dt, elapsed) {
    if (this.state === STATE.IDLE) return;
    // The primary is out of action for the whole deploy-fire-stow window.
    this.system.overheat.hold();
    this._timer -= dt;
    void elapsed;
    if (this._timer > 0) return;

    if (this.state === STATE.DEPLOYING) {
      this._fire();
      this.state = STATE.STOWING;
      this._timer = Math.max(0.01, this.config.stow);
    } else {
      this.state = STATE.IDLE;
      this._timer = 0;
    }
    this._publish(true);
  }

  /* ---------------------------------- fire --------------------------------- */

  _fire() {
    const cfg = this.config;
    if (cfg.ammo <= 0) return;
    this._ensurePool();
    const slot = this.rounds.find((r) => !r.live);
    if (!slot) return;

    const cam = this.ctx.camera;
    cam.updateMatrixWorld();
    _dir.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();
    // Loft about the camera's own right axis so the arc is always "up" relative to
    // the view, including while leaning or rolled.
    _a.set(1, 0, 0).applyQuaternion(cam.quaternion).normalize();
    _dir.applyAxisAngle(_a, TUNING.loft).normalize();

    slot.live = true;
    slot.mode = cfg.mode;
    slot.t = 0;
    slot.grounded = false;
    slot.fuse = 0;
    slot.pos
      .setFromMatrixPosition(cam.matrixWorld)
      .addScaledVector(_dir, TUNING.spawnAhead)
      .addScaledVector(_a, 0.05);
    slot.pos.y -= TUNING.spawnDown;
    slot.vel.copy(_dir).multiplyScalar(cfg.muzzleVelocity);
    // Inherit the player's own motion: firing on the run should throw further.
    const p = this.ctx.player;
    if (p) slot.vel.addScaledVector(p.velocity, 0.35);

    slot.mesh.material = this._mats[slot.mode] ?? this._mats.grenade;
    slot.mesh.position.copy(slot.pos);
    slot.mesh.visible = true;

    cfg.ammo = Math.max(0, cfg.ammo - 1);
    this.fired++;

    this.ctx.events.emit('audio:play', {
      id: 'weapon_switch',
      volume: 0.8,
      pitch: 0.55,
    });
    this._publish(true);
  }

  /* -------------------------------- flight --------------------------------- */

  _stepRounds(dt) {
    const phys = this.ctx.physics;
    for (let i = 0; i < this.rounds.length; i++) {
      const r = this.rounds[i];
      if (!r.live) continue;
      r.t += dt;

      if (r.grounded) {
        // A landed smoke canister ticking down to bloom.
        r.fuse -= dt;
        if (r.fuse <= 0) this._deployPayload(r, r.pos, null);
        continue;
      }

      const steps = TUNING.substeps;
      const h = dt / steps;
      let detonated = false;
      for (let s = 0; s < steps && !detonated; s++) {
        r.vel.y -= TUNING.gravity * h;
        _from.copy(r.pos);
        _b.copy(r.vel).multiplyScalar(h);
        const dist = _b.length();
        if (dist > 1e-5 && phys) {
          _dir.copy(_b).multiplyScalar(1 / dist);
          // Actors are included: a round that hits a body must go off on the body.
          const hit = phys.raycast(_from, _dir, dist + TUNING.radius, MASK.ALL, this.ctx.player);
          if (hit?.hit) {
            r.pos.copy(hit.point).addScaledVector(hit.normal ?? _up, TUNING.radius + 0.02);
            this._onImpact(r, hit);
            detonated = true;
            break;
          }
        }
        r.pos.add(_b);
      }
      if (detonated) continue;

      r.mesh.position.copy(r.pos);
      if (r.t > TUNING.maxFlight) this._deployPayload(r, r.pos, null);
    }
  }

  _onImpact(r, hit) {
    if (r.mode === 'smoke') {
      // The canister lands and burns for a beat before it blooms. It also stops
      // where it hit rather than bouncing, which keeps a thrown smoke predictable.
      r.grounded = true;
      r.fuse = TUNING.smokeFuse;
      r.mesh.position.copy(r.pos);
      this.ctx.events.emit('audio:play', {
        id: 'impact_metal',
        position: r.pos.clone(),
        volume: 0.5,
      });
      return;
    }

    // Unarmed HE just stops. This is what stops the launcher being a shotgun.
    if (r.t < TUNING.armTime) {
      r.grounded = true;
      r.fuse = TUNING.armTime - r.t + 0.05;
      r.mode = 'grenade';
      return;
    }
    this._deployPayload(r, r.pos, hit);
  }

  _deployPayload(r, pos, hit) {
    r.live = false;
    r.mesh.visible = false;
    if (r.mode === 'smoke') {
      // Sit the cloud's centre a little above the canister so it fills a standing
      // silhouette rather than a puddle.
      this.smoke.deploy(pos.x, pos.y + 1.1, pos.z);
      this.ctx.events.emit('audio:play', { id: 'explosion', position: pos.clone(), volume: 0.35, pitch: 0.5 });
      return;
    }
    this._explode(pos, hit);
  }

  /**
   * Explosion. `fx:explosion` is the event the FX system, camera rig and post stack
   * already consume, so the visuals, the shake and the flash come for free; the
   * damage goes through `match.requestDamage`, the same funnel the ballistics
   * path uses, so the blast obeys whatever the mode says about friendly fire.
   */
  _explode(pos, hit) {
    const cfg = this.config;
    const ctx = this.ctx;
    ctx.events.emit('fx:explosion', { position: pos.clone(), power: TUNING.explosionPower });
    ctx.events.emit('audio:play', { id: 'explosion', position: pos.clone(), volume: 0.95 });
    if (hit && hit.point && !hit.actor) {
      ctx.events.emit('hit:surface', {
        point: hit.point,
        normal: hit.normal,
        material: hit.materialId ?? 'concrete',
        entity: null,
      });
    }

    /**
     * One sweep over everyone alive, rather than the enemy list plus a hand-written
     * case for the player. The two used to differ by more than the self-damage
     * scale — the enemy aim point was 1.05 m up and the player's 1.00 — which meant
     * a grenade at your feet and the same grenade at a bot's feet were measured from
     * subtly different places. They now use the same one.
     */
    const radius = Math.max(0.5, cfg.radius);
    const owner = ctx.player ?? null;
    const match = ctx.match;
    for (const reg of match?.living() ?? []) {
      const e = reg.actor;
      if (!e.position || typeof e.health !== 'number') continue;
      _a.set(e.position.x, e.position.y + 1.05, e.position.z);
      const d = _a.distanceTo(pos);
      if (d > radius) continue;
      // Blast needs line of sight: a wall between you and the grenade is cover.
      // Uses the *original* visibility, not the smoke-wrapped one — smoke does
      // not stop a shockwave.
      if (!this._blastLine(pos, _a)) continue;
      const falloff = Math.max(TUNING.splashMinFraction, 1 - d / radius);
      const self = e === owner;
      _b.subVectors(_a, pos).normalize();
      match.requestDamage({
        target: e,
        source: owner,
        amount: cfg.damage * falloff * (self ? TUNING.selfDamageScale : 1),
        hitbox: null,
        point: _a.clone(),
        normal: _b.clone().negate(),
        dir: _b.clone(),
        means: MEANS.EXPLOSIVE,
        weapon: 'launcher',
        splash: true,
      });
    }
  }

  /** Line of sight for blast, deliberately ignoring smoke. */
  _blastLine(from, to) {
    const phys = this.ctx.physics;
    if (!phys) return true;
    _dir.subVectors(to, from);
    const dist = _dir.length();
    if (dist < 1e-4) return true;
    _dir.multiplyScalar(1 / dist);
    return !phys.raycast(from, _dir, dist - 0.05, SOLID).hit;
  }

  /* --------------------------------- readout -------------------------------- */

  _publish(force) {
    const cfg = this.config;
    const key = `${cfg.mode}|${cfg.ammo}|${this.state}`;
    if (!force && key === this._published) return;
    this._published = key;
    this.ctx.events.emit('launcher:state', {
      mode: cfg.mode,
      ammo: cfg.ammo,
      max: cfg.max,
      /** 'idle' | 'deploying' | 'stowing' */
      state: this.state,
      /** True when a press would actually launch something. */
      ready: cfg.mode !== 'none' && cfg.ammo > 0 && this.state === STATE.IDLE,
    });
  }

  report() {
    return {
      mode: this.config.mode,
      ammo: this.config.ammo,
      state: this.state,
      fired: this.fired,
      inFlight: this._liveCount(),
    };
  }

  dispose() {
    if (this._group) {
      this.ctx.scene.remove(this._group);
      this._geo?.dispose();
      for (const k in this._mats) this._mats[k].dispose();
      this._group = null;
    }
    this.rounds.length = 0;
    void this._scratch;
  }
}

const _up = new THREE.Vector3(0, 1, 0);
